import { useState, useEffect } from 'react';
import { X, Download, ExternalLink, FileText, Image as ImageIcon, File, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import type { ClientDocument } from '@/types/client';
import { formatFileSize, DOCUMENT_CATEGORY_LABELS } from '@/types/client';
import { clientsService } from '@/services/api/clients';
import { toast } from 'sonner';

interface DocumentPreviewDialogProps {
  document: ClientDocument | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DocumentPreviewDialog({ document, open, onOpenChange }: DocumentPreviewDialogProps) {
  const [loading, setLoading] = useState(true);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [urlLoading, setUrlLoading] = useState(false);

  const isPDF = document?.mimeType === 'application/pdf' || document?.fileType?.toLowerCase() === 'pdf';
  const isImage = document?.mimeType?.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(document?.fileType?.toLowerCase() || '');
  const isPreviewable = isPDF || isImage;

  // Fetch presigned URL when dialog opens
  useEffect(() => {
    if (open && document?.id) {
      setUrlLoading(true);
      setFileUrl(null);
      setLoading(true);
      clientsService.getFileDownloadUrl(document.id)
        .then((data) => {
          setFileUrl(data.downloadUrl);
        })
        .catch((err) => {
          console.error('Failed to get download URL:', err);
          toast.error('Failed to load file preview');
        })
        .finally(() => {
          setUrlLoading(false);
        });
    }
  }, [open, document?.id]);

  if (!document) return null;

  const handleDownload = async () => {
    try {
      const url = fileUrl || (await clientsService.getFileDownloadUrl(document.id)).downloadUrl;
      const link = window.document.createElement('a');
      link.href = url;
      link.download = document.originalFilename;
      link.click();
    } catch (err) {
      toast.error('Failed to download file');
    }
  };

  const handleOpenExternal = async () => {
    try {
      const url = fileUrl || (await clientsService.getFileDownloadUrl(document.id)).downloadUrl;
      window.open(url, '_blank');
    } catch (err) {
      toast.error('Failed to open file');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[90vh] p-0 flex flex-col">
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b">
          <div className="flex items-start justify-between">
            <div className="flex-1 pr-4">
              <DialogTitle className="text-lg font-semibold mb-2">{document.originalFilename}</DialogTitle>
              <div className="flex items-center gap-2 flex-wrap">
                {document.documentCategory && (
                  <Badge variant="outline">
                    {DOCUMENT_CATEGORY_LABELS[document.documentCategory as keyof typeof DOCUMENT_CATEGORY_LABELS] || document.documentCategory}
                  </Badge>
                )}
                <Badge variant="secondary" className="text-xs">
                  {document.fileType.toUpperCase()}
                </Badge>
                <Badge variant="secondary" className="text-xs">
                  {formatFileSize(document.fileSizeBytes)}
                </Badge>
                {document.financialYear && (
                  <Badge variant="outline" className="text-xs">
                    FY{document.financialYear}
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleDownload}>
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
              <Button variant="outline" size="sm" onClick={handleOpenExternal}>
                <ExternalLink className="h-4 w-4 mr-2" />
                Open
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Preview Area */}
        <div className="flex-1 overflow-hidden bg-muted/20">
          {urlLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Loader2 className="h-12 w-12 text-muted-foreground mx-auto mb-4 animate-spin" />
                <p className="text-sm text-muted-foreground">Loading file...</p>
              </div>
            </div>
          ) : isPreviewable && fileUrl ? (
            <ScrollArea className="h-full">
              <div className="p-6">
                {isPDF ? (
                  <div className="bg-white rounded-lg shadow-sm">
                    {loading && (
                      <div className="flex items-center justify-center h-96">
                        <div className="text-center">
                          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                          <p className="text-sm text-muted-foreground">Loading PDF...</p>
                        </div>
                      </div>
                    )}
                    <iframe
                      src={`${fileUrl}#view=FitH`}
                      className="w-full h-[calc(90vh-200px)] border-0"
                      title={document.originalFilename}
                      onLoad={() => setLoading(false)}
                    />
                  </div>
                ) : isImage ? (
                  <div className="flex items-center justify-center">
                    <img
                      src={fileUrl}
                      alt={document.originalFilename}
                      className="max-w-full h-auto rounded-lg shadow-sm"
                      onLoad={() => setLoading(false)}
                    />
                  </div>
                ) : null}
              </div>
            </ScrollArea>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center p-6">
              <File className="h-20 w-20 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">Preview Not Available</h3>
              <p className="text-sm text-muted-foreground mb-6 max-w-md">
                This file type cannot be previewed in the browser. Please download the file to view it.
              </p>
              <Button onClick={handleDownload}>
                <Download className="h-4 w-4 mr-2" />
                Download File
              </Button>
            </div>
          )}
        </div>

        {/* Footer with metadata */}
        {document.description || document.aiSummary || document.tags?.length ? (
          <>
            <Separator />
            <div className="px-6 py-4 bg-card">
              <ScrollArea className="max-h-32">
                {document.description && (
                  <div className="mb-3">
                    <p className="text-xs text-muted-foreground mb-1">Description</p>
                    <p className="text-sm">{document.description}</p>
                  </div>
                )}
                {document.aiSummary && (
                  <div className="mb-3">
                    <p className="text-xs text-muted-foreground mb-1">AI Summary</p>
                    <p className="text-sm">{document.aiSummary}</p>
                  </div>
                )}
                {document.tags && document.tags.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Tags</p>
                    <div className="flex flex-wrap gap-1">
                      {document.tags.map((tag, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </ScrollArea>
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
